import { IdealBankElement } from '@stripe/react-stripe-js';
import { Formik, Form } from 'formik';
import { useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import Button from 'hb-react/backoffice/components/Button';
import StripeError from 'hb-react/frontoffice/components/payments/PaymentStripe/errors/StripeError';
import {
  displayAmount,
  displayCurrency,
  cartItemWithRecurringPayment,
  getReturnUrl,
} from 'hb-react/frontoffice/components/payments/PaymentStripe/utils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { Label } from 'hb-react/shared/components/DesignSystem/Input';
import { Text } from 'hb-react/shared/components/formik/components/inputs';
import Loader from 'hb-react/shared/components/Loader';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import MandateText from '../MandateText';

import './IdealBankSectionStyles.css';

const IDEAL_ELEMENT_OPTIONS = {
  style: {
    base: {
      padding: '10px 12px',
      color: '#32325d',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
};

const IdealPayment = ({
  amount,
  currency,
  endpointUrl,
  endpointData,
  stripe,
  elements,
  paymentAccountConfig,
}) => {
  const [loading, setLoading] = useState(false);
  const [payButtonDisabled, setPayButtonDisabled] = useState(true);

  const { currentUser } = useDefaultAppContexts();

  const handleError = (error) => {
    displayNotif(
      'error',
      s__('StripeIdeal|An error occurred processing the payment'),
    );
    setLoading(false);
    errorReporting.captureException(error);
  };

  const recurringPaymentEnabled = cartItemWithRecurringPayment(endpointData);

  const confirmPaymentIntent = (
    clientSecret,
    cartId,
    accountholderName,
    accountholderEmail,
  ) => {
    const idealBank = elements.getElement(IdealBankElement);

    const returnUrl = getReturnUrl({ cart_id: cartId });

    const billingDetails = {
      name: accountholderName,
    };

    if (recurringPaymentEnabled) {
      billingDetails.email = accountholderEmail;
    }

    stripe
      .confirmIdealPayment(clientSecret, {
        payment_method: {
          ideal: idealBank,
          billing_details: billingDetails,
        },
        return_url: returnUrl,
      })
      .then(({ error }) => {
        if (error) {
          handleError(new StripeError('Error confirming ideal payment', error));
        }
      });
  };

  const setupIdealPayment = () => {
    const body = endpointData({
      payment_method: 'ideal',
      gateway: 'Stripe',
    });

    return axios.post(endpointUrl, body).catch((error) => {
      handleError(
        new StripeError('Error creating ideal payment intent', error),
      );
    });
  };

  const handleSubmit = ({ accountholderName, accountholderEmail }) => {
    setLoading(true);
    setupIdealPayment().then(({ data }) => {
      const { client_secret: clientSecret, cart_id: cartId } = data;

      confirmPaymentIntent(
        clientSecret,
        cartId,
        accountholderName,
        accountholderEmail,
      );
    });
  };

  const initialValues = {
    accountholderName: currentUser?.name || '',
    accountholderEmail: currentUser?.email || '',
  };

  return (
    <Card>
      <Formik onSubmit={handleSubmit} initialValues={initialValues}>
        {() => (
          <Form>
            {loading && <Loader fillParent />}
            <Box
              minHeight={recurringPaymentEnabled ? 'auto' : '270px'}
              flexDirection="column"
            >
              <Box data-testid="stripe-ideal-wrapper" mb={4} display="block">
                <Label for="ideal-bank-element">
                  {s__('StripeIdeal|Bank')}
                </Label>
                <IdealBankElement
                  id="ideal-bank-element"
                  options={IDEAL_ELEMENT_OPTIONS}
                  onChange={() => {
                    setPayButtonDisabled(false);
                  }}
                />
              </Box>
              <Box flexDirection="column" mb={2}>
                <Text
                  name="accountholderName"
                  label={s__('StripeIdeal|Accountholder Name')}
                  validate={{
                    required: true,
                  }}
                />
                {recurringPaymentEnabled && (
                  <Text
                    name="accountholderEmail"
                    label={s__('StripeIdeal|Accountholder Email')}
                    validate={{
                      required: true,
                    }}
                  />
                )}

                {recurringPaymentEnabled && (
                  <MandateText
                    merchantDisplayName={
                      paymentAccountConfig.merchant_display_name
                    }
                  />
                )}
              </Box>
              <Button
                className="push--top"
                data-testid="stripe-sepa-debit-form-submit"
                type="submit"
                purpose="primary"
                onSubmit={handleSubmit}
                disabled={payButtonDisabled}
              >
                {__('Pay %{currency} %{amount}').i__({
                  currency: displayCurrency(currency),
                  amount: displayAmount(amount),
                })}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default IdealPayment;
